import { api } from '@/lib/api'
import { Job, JobStatus } from '@/lib/types'

function mapJob(raw: any): Job {
  // Customer is included from Prisma when available — prefer live customer
  // values over the historical snapshot on the Job row so the accordion
  // shows the customer's latest email/address parts.
  const customer = raw.customer ?? {}
  return {
    id: raw.id,
    accountId: raw.accountId,
    customerId: raw.customerId ?? undefined,
    customerName: raw.customerName,
    customerPhone: raw.customerPhone ?? customer.phone ?? undefined,
    customerEmail: customer.email ?? undefined,
    customerAddress: raw.customerAddress ?? undefined,
    customerNotes: customer.notes ?? null,
    customerStreet: customer.street ?? undefined,
    customerCity: customer.city ?? undefined,
    customerState: customer.state ?? undefined,
    customerZip: customer.zip ?? undefined,
    serviceStreet: raw.serviceStreet ?? undefined,
    serviceCity: raw.serviceCity ?? undefined,
    serviceState: raw.serviceState ?? undefined,
    serviceZip: raw.serviceZip ?? undefined,
    description: raw.description ?? undefined,
    type: raw.type ?? 'OTHER',
    urgency: raw.urgency ?? 'LOW',
    status: raw.status,
    completedAt: raw.completedAt ?? undefined,
    completionNotes: raw.completionNotes ?? undefined,
    notes: raw.notes ?? undefined,
    assignedUserId: raw.assignedUserId ?? null,
    assignedUser: raw.assignedUser ?? null,
    bookingAnswers: raw.bookingAnswers ?? undefined,
    invoice: raw.invoice ? { id: raw.invoice.id, status: raw.invoice.status } : null,
    createdAt: raw.createdAt,
  }
}

export async function getJob(jobId: string): Promise<Job> {
  const raw = await api<any>(`/jobs/${jobId}`)
  return mapJob(raw)
}

export async function listJobs(
  accountId: string,
  filters?: { status?: JobStatus; statuses?: JobStatus[]; customerId?: string; assignedUserId?: string; cursor?: string; limit?: number; order?: 'fifo' | 'lifo' },
): Promise<{ jobs: Job[]; hasMore: boolean; nextCursor: string | null }> {
  const params = new URLSearchParams({ accountId })
  if (filters?.status) params.set('status', filters.status)
  if (filters?.statuses && filters.statuses.length > 0) params.set('statuses', filters.statuses.join(','))
  if (filters?.customerId) params.set('customerId', filters.customerId)
  // 'me' (server resolves to the caller) or a teammate id, or 'unassigned'.
  if (filters?.assignedUserId) params.set('assignedUserId', filters.assignedUserId)
  if (filters?.limit) params.set('limit', String(filters.limit))
  if (filters?.cursor) params.set('cursor', filters.cursor)
  if (filters?.order) params.set('order', filters.order)

  const raw = await api<any>(`/jobs?${params}`)
  return {
    jobs: (raw.jobs ?? []).map(mapJob),
    hasMore: raw.hasMore ?? false,
    nextCursor: raw.nextCursor ?? null,
  }
}

export async function createJob(data: {
  accountId: string
  customerId: string
  description: string
  type?: string
  urgency?: string
}): Promise<Job> {
  const raw = await api<any>('/jobs', {
    method: 'POST',
    body: JSON.stringify(data),
  })
  return mapJob(raw)
}

export async function updateJob(
  jobId: string,
  updates: { description?: string; type?: string; customerAddress?: string; status?: JobStatus; completionNotes?: string; notes?: string; completedAt?: string },
): Promise<void> {
  await api(`/jobs/${jobId}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  })
}

// Claim (userId = caller's id), release (userId = null), or reassign (userId =
// another active member). The server enforces "only OWNER may touch another
// teammate's job". Returns the updated job (with assignedUser populated).
export async function assignJob(jobId: string, userId: string | null): Promise<Job> {
  const raw = await api<any>(`/jobs/${jobId}/assign`, {
    method: 'PATCH',
    body: JSON.stringify({ userId }),
  })
  return mapJob(raw)
}

export async function deleteJob(jobId: string): Promise<void> {
  await api(`/jobs/${jobId}`, { method: 'DELETE' })
}

export type JobMedia = { id: string; storageUrl: string; mimeType: string | null }

// Photos attached to a job (booking-form uploads + rehosted MMS).
export async function getJobMedia(jobId: string): Promise<JobMedia[]> {
  return api<JobMedia[]>(`/jobs/${jobId}/media`)
}
